import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useConversations, useMessagesUnread } from '../lib/queries';
import { ChevronLeft, MessageSquare, Inbox, Mail, Loader2 } from 'lucide-react';

/**
 * Inbox page. Two tabs:
 *  • Inbox      — accepted conversations + your sent requests
 *  • Requests   — incoming pending requests (someone else initiated)
 *
 * Each row deep-links to /app/messaggi/:conversation_id.
 */
export default function Messages() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const [tab, setTab] = useState('inbox');

  const { data: convs = [], isLoading } = useConversations(tab);
  const { data: counts } = useMessagesUnread();
  const pendingCount = counts?.pending_requests || 0;

  return (
    <div
      className={`flex flex-col ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}
      style={{
        // Anchor the inbox to the viewport so the OS status bar
        // doesn't overlap the header and the page itself never
        // scrolls — only the conversation list does.
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        // Leave room for the fixed BottomNav (~64-80px) so the last
        // conversation row isn't hidden under it.
        bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
        zIndex: 50,
      }}
      data-testid="messages-page"
    >
      {/* Header — flex child, never scrolls. Respects iOS notch. */}
      <div
        className={`flex-shrink-0 px-4 pb-3 border-b ${
          isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'
        }`}
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 0px))' }}
      >
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              data-testid="messages-back-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
            </button>
            <h1 className={`font-display text-xl flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('messages.title')}
            </h1>
          </div>

          {/* Tabs */}
          <div className={`p-1 rounded-2xl flex ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
            <TabButton
              active={tab === 'inbox'}
              onClick={() => setTab('inbox')}
              icon={<Inbox className="w-4 h-4" />}
              label={t('messages.inbox')}
              testid="messages-tab-inbox"
              isDark={isDark}
            />
            <TabButton
              active={tab === 'requests'}
              onClick={() => setTab('requests')}
              icon={<Mail className="w-4 h-4" />}
              label={
                pendingCount > 0
                  ? t('messages.requestsBadge', { count: pendingCount })
                  : t('messages.requests')
              }
              testid="messages-tab-requests"
              isDark={isDark}
            />
          </div>
        </div>
      </div>

      {/* Body — flex-1 takes all remaining vertical space.
          Scrollbar appears only when the conversation list overflows. */}
      <div
        className={`flex-1 min-h-0 overflow-y-auto px-4 ${
          isDark ? 'messages-scroll-dark' : 'messages-scroll-light'
        }`}
        data-testid="messages-scroll-container"
      >
        <div className="max-w-md mx-auto py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#E8334A]" />
            </div>
          ) : convs.length === 0 ? (
            <EmptyState
              isDark={isDark}
              title={tab === 'inbox' ? t('messages.empty') : t('messages.emptyRequests')}
              subtitle={
                tab === 'inbox'
                  ? t('messages.emptySubtitle')
                  : t('messages.emptyRequestsSubtitle')
              }
            />
          ) : (
            <div className="space-y-2">
              {convs.map((c) => (
                <ConversationRow
                  key={c.conversation_id}
                  conv={c}
                  isDark={isDark}
                  language={language}
                  t={t}
                  onClick={() => navigate(`/app/messaggi/${c.conversation_id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testid, isDark }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium text-sm transition-all ${
        active
          ? 'bg-[#E8334A] text-white shadow-sm'
          : isDark
          ? 'text-white/60 hover:text-white'
          : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function EmptyState({ isDark, title, subtitle }) {
  return (
    <div
      className={`text-center py-20 ${isDark ? 'text-white/50' : 'text-gray-500'}`}
      data-testid="messages-empty"
    >
      <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
      <p className="font-medium text-base">{title}</p>
      <p className="text-sm mt-1">{subtitle}</p>
    </div>
  );
}

function ConversationRow({ conv, isDark, language, t, onClick }) {
  const peer = conv.other_user || {};
  const peerName = peer.name || peer.username || '…';
  const lastMsg = conv.last_message;
  const isPending = conv.status === 'pending';
  const unread = conv.unread_count || 0;

  const preview = useMemo(() => {
    if (!lastMsg) return t('messages.pendingNote');
    if (lastMsg.type === 'image') return `📷 ${t('messages.photo')}`;
    if (lastMsg.type === 'text') return lastMsg.text || '';
    return `[${lastMsg.type}]`;
  }, [lastMsg, t]);

  const time = useMemo(() => {
    if (!conv.last_message_at) return '';
    const d = new Date(conv.last_message_at);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return t('messages.yesterday');
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short' });
  }, [conv.last_message_at, language, t]);

  return (
    <button
      onClick={onClick}
      data-testid={`conversation-row-${conv.conversation_id}`}
      className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-colors ${
        isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:bg-gray-50 border border-gray-100'
      }`}
    >
      <Avatar src={peer.picture} name={peerName} isDark={isDark} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            {peerName}
          </span>
          <span className={`text-[11px] flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            {time}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <p
            className={`flex-1 text-sm truncate ${
              unread > 0
                ? isDark
                  ? 'text-white font-medium'
                  : 'text-gray-900 font-medium'
                : isDark
                ? 'text-white/60'
                : 'text-gray-500'
            }`}
          >
            {isPending && !lastMsg ? (
              <span className="italic">{t('messages.pendingNote')}</span>
            ) : (
              preview
            )}
          </p>
          {unread > 0 && (
            <span
              data-testid={`conv-unread-${conv.conversation_id}`}
              className="min-w-5 h-5 px-1.5 rounded-full bg-[#E8334A] text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function Avatar({ src, name, isDark }) {
  if (src) {
    return (
      <img
        src={src.startsWith('/api/') ? src : src}
        alt={name}
        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
      />
    );
  }
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div
      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-base ${
        isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
      }`}
    >
      {initial}
    </div>
  );
}
